// MyCV — the 简历 page (/my-cv, UK-CN only): the user's ONE base CV and the
// job keywords derived from it. Split out of the tailor page by user decision
// (2026-08-14): tailoring is a production line, this page is the identity it
// runs on — the base CV feeds evaluation and per-role tailoring, the keywords
// feed the board's 为你推荐.
//
// /cv/new (generate from Chinese), /cv/upload and /cv/editor stay separate
// sub-pages; this page is where they are reached from.

import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPut } from '../lib/api';
import { useZh, useProductReady } from '../lib/useProduct';
import { CartesianBuilder, allCombos, useAnchors, usePositions } from '../components/domain/anchors';

interface CVStatus { file: string | null; chars: number; preview: string }

/**
 * Inline viewer/editor for the base CV's raw text. This one DOES write the
 * CV file itself — that is the whole point of the 查看/编辑 button. It used
 * to navigate to /cv/editor, which is the per-role TAILOR surface and
 * bounces without a selected match (user report, 2026-08-14).
 */
function CvTextPanel({ onSaved }: { onSaved: () => void }) {
  const q = useQuery({
    queryKey: ['cv-content'],
    queryFn: () => apiGet<{ file: string | null; text: string }>('/api/cv-content'),
    staleTime: 0,
  });
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const shown = text ?? q.data?.text ?? '';
  const dirty = text != null && text !== q.data?.text;
  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      await apiPut('/api/cv-content', { text: shown });
      setText(null);
      await q.refetch();
      onSaved();
    } catch {
      setErr('保存失败——正文至少需要 100 个字符。');
    } finally {
      setBusy(false);
    }
  };
  if (q.isLoading) return <div className="page-sub" style={{ padding: '10px 0' }}>加载简历原文…</div>;
  return (
    <div style={{ marginTop: 12, width: '100%' }}>
      <textarea
        value={shown}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
        style={{
          width: '100%', minHeight: 320, boxSizing: 'border-box',
          padding: '12px 14px', borderRadius: 8, border: '1px solid var(--line)',
          background: 'var(--surface)', color: 'var(--ink)',
          fontFamily: 'ui-monospace, monospace', fontSize: 12.5, lineHeight: 1.6,
        }}
      />
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <button className="btn accent" disabled={!dirty || busy} onClick={save}>
          {busy ? '保存中…' : dirty ? '保存简历' : '未改动'}
        </button>
        <span className="page-sub" style={{ marginTop: 0, fontSize: 12 }}>
          这是简历文件本身——保存会直接更新它。
        </span>
        {err && <span className="page-sub" style={{ marginTop: 0, color: '#b45309' }}>{err}</span>}
      </div>
    </div>
  );
}

export default function MyCV() {
  const zh = useZh();
  const productReady = useProductReady();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [cvOpen, setCvOpen] = useState(false);

  const cvQ = useQuery({
    queryKey: ['cv-status'],
    queryFn: () => apiGet<CVStatus>('/api/cv-status'),
    staleTime: 60_000,
  });
  const a = useAnchors(true);
  const pos = usePositions(true);

  if (!productReady) return null;
  if (!zh) return <Navigate to="/matches" replace />;

  const hasCv = !!(cvQ.data?.chars && cvQ.data.chars > 100);

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="简历 · CV"
        title="你的简历，从这里开始。"
        sub="一份英文基础简历，加上你想找的职位关键词——职位板的推荐、岗位评估和按岗位定制都建立在这两样上面。"
      />

      {/* ── 我的简历 ─────────────────────────────────────────────── */}
      {cvQ.isSuccess && !hasCv && (
        <div className="card" style={{ padding: '24px 26px', marginBottom: 16, borderLeft: '3px solid var(--pri)' }}>
          <div className="eyebrow">第一步</div>
          <div className="serif" style={{ fontSize: 21, margin: '2px 0 6px' }}>先准备一份英文简历</div>
          <div style={{ fontSize: 13.5, color: 'var(--ink2)', lineHeight: 1.7, marginBottom: 14, maxWidth: 560 }}>
            不需要先有英文简历——用中文写你的经历，AI 帮你写成英文；已经有的话直接上传。
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="btn accent" onClick={() => nav('/cv/new')}>用中文生成简历 →</button>
            <button className="btn" onClick={() => nav('/cv/upload')}>上传已有简历</button>
            <Link to="/profile" className="page-sub" style={{ marginTop: 0, textDecoration: 'underline' }}>
              顺便完善姓名和联系方式 →
            </Link>
          </div>
        </div>
      )}
      {cvQ.isSuccess && hasCv && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <FileText size={17} style={{ color: 'var(--pri)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>我的简历</span>
            <span className="page-sub" style={{ marginTop: 0, marginLeft: 8, fontSize: 12.5 }}>
              {cvQ.data!.file} · {cvQ.data!.chars.toLocaleString()} 字符
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn ghost" onClick={() => setCvOpen((v) => !v)}>
              {cvOpen ? '收起' : '查看 / 编辑'}
            </button>
            <button className="btn ghost" onClick={() => nav('/cv/new')}>重新生成</button>
            <button className="btn ghost" onClick={() => nav('/cv/upload')}>重新上传</button>
          </div>
          {cvOpen && (
            <CvTextPanel onSaved={() => void qc.invalidateQueries({ queryKey: ['cv-status'] })} />
          )}
        </div>
      )}

      {/* ── 职位关键词 ───────────────────────────────────────────── */}
      <div className="card" style={{ padding: '22px 24px' }}>
        <div className="tab" style={{ marginBottom: 8 }}>职位关键词</div>
        <CartesianBuilder a={a} pos={pos} hasCv={hasCv} />
        {allCombos(a.anchors, pos.positions, pos.combos).length > 0 && (
          <div style={{ marginTop: 14 }}>
            <Link to="/board?rec=1" style={{ color: 'var(--pri)', fontSize: 13 }}>
              去职位板看推荐结果 →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
